import { useEffect, useState } from "react";

import { AlertBar2 } from "@/ui2/atoms/AlertBar";
import { Heading2 } from "@/ui2/atoms/Heading";
import { ModalControlled2 } from "@/ui2/molecules/Modal";
import { useGetPresignedUrl } from "@/api2/S3/queries/useGetPresignedUrl";
import type { UUID } from "@/core/types";

import { IdentityDocumentPreviewImage } from "./IdentityDocumentPreviewImage";
import { IdentityDocumentPreviewLoader } from "./IdentityDocumentPreviewLoader";

type IdentityDocumentPreviewModalProps = {
  onOpenChange: (isOpen: boolean) => void;
  isModalOpen: boolean;
  entityId: UUID;
};

export function IdentityDocumentPreviewModal({
  onOpenChange,
  isModalOpen,
  entityId,
}: IdentityDocumentPreviewModalProps) {
  const { data, isLoading, error } = useGetPresignedUrl({
    payload: {
      entity_id: entityId,
      entity_type: "patient_personal_id_image",
      file_sub_type: "PERSONAL_ID_FRONT",
      file_type: "PATIENT_PERSONAL_ID_IMAGE",
    },
    options: {
      /** Fetch presigned url only when modal is open */
      enabled: isModalOpen,
    },
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  /** Reset image load state when modal opens
   * Useful when user has cache disabled and reopens the modal
   */
  useEffect(() => {
    if (!isModalOpen) return;

    setIsImageLoaded(false);
  }, [isModalOpen]);

  return (
    <ModalControlled2 isOpen={isModalOpen} onOpenChange={onOpenChange}>
      <div className="tw:flex tw:flex-col tw:gap-6">
        <Heading2 size={2}>View ID</Heading2>

        <Divider />

        <div className="tw:flex tw:aspect-3/2 tw:h-82.5 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-stroke-quaternary tw:p-3">
          {error && (
            <AlertBar2 variant="error">
              <b>Preview Error: </b>An error occurred while fetching the document. Please try again.
            </AlertBar2>
          )}

          {isLoading || !isImageLoaded ? <IdentityDocumentPreviewLoader /> : null}

          {data && (
            <IdentityDocumentPreviewImage
              src={data.message}
              onImageLoad={() => setIsImageLoaded(true)}
              isImageLoaded={isImageLoaded}
            />
          )}
        </div>
      </div>
    </ModalControlled2>
  );
}

const Divider = () => (
  <div className="tw:-left-3 tw:h-px tw:w-[calc(100%+theme(spacing.12))] tw:-translate-x-6 tw:bg-stroke-quaternary" />
);
